import React, {
  ReactElement,
  cloneElement,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useClick,
  useInteractions,
  FloatingFocusManager,
  Placement,
  arrow,
  FloatingPortal,
  useRole,
  useMergeRefs,
} from "@floating-ui/react";
import { PopoverProps } from "./popover.types";
import classNames from "classnames";
import { get } from "../utils";
import { useCombinedPopoverRefs } from "./useCombinedPopoverRefs";
import { ConditionalWrapper } from "../utils/ConditionalWrapper";
import { getArrowCoords } from "../utils/dom/floating-ui-utils/getArrowCoords";
import { matchPositionKey } from "../utils/dom/floating-ui-utils/matchPositionKey";
import {
  Arrow,
  matchCaretKey,
} from "../utils/dom/floating-ui-utils/matchCaretKey";

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      anchorX,
      anchorY,
      children,
      className,
      closeButton,
      dismissible = true,
      id,
      onClose,
      onOpenChange: setControlledOpen,
      open: controlledOpen,
      toggleEl,
      toggleElWrapperStyle,
      toggleElWrapperClassName,
      toggleElWrapperRef,
      contentCloseElQuery,
      autoFlip = true,
      onClick,
      portalWrapperClassName,
      ...rest
    },
    containerExternalRef
  ): ReactElement => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;
    const arrowRef = useRef(null);
    const [placementProp, setPlacementProp] = useState<Placement | undefined>();
    const [arrowDirection, setArrowDirection] = useState<Arrow | undefined>();

    const childType: any = toggleEl?.type;
    const isBadge =
      React.isValidElement(toggleEl) &&
      (childType.displayName === "Badge" || childType.displayName === "Avatar");

    const { refs, floatingStyles, context, middlewareData, placement } =
      useFloating({
        open,
        onOpenChange: (isOpen, e) => {
          setOpen(isOpen);
          if (!isOpen && e) {
            onClose?.(e);
          }
        },
        // use pre-defined positions
        placement: placementProp,
        // middleware are options to position (and customize) the popover
        middleware: [
          offset({
            mainAxis: 8,
            // crossAxis: 130, // for custom use cases when pre-define postions will not solve problem
          }),
          // popover flips to any placement around the target element
          flip({
            fallbackAxisSideDirection: "end",
            fallbackPlacements: !autoFlip ? [] : undefined,
          }),
          // popover shifts only along an axis
          shift({ mainAxis: true, crossAxis: false }),
          arrow({
            element: arrowRef,
          }),
        ],
        transform: false,
        whileElementsMounted: autoUpdate,
      });

    useCombinedPopoverRefs(refs.floating, containerExternalRef);
    const toggleElRef = useMergeRefs([
      refs.setReference,
      (toggleEl as any)?.ref,
    ]);

    useEffect(() => {
      setPlacementProp(matchPositionKey(`${anchorX}-${anchorY}`));
      setArrowDirection(matchCaretKey(placement));
    }, [placement, anchorX, anchorY]);

    const click = useClick(context, {
      enabled: controlledOpen == null,
    });

    const dismissal = useDismiss(context, {
      enabled: true,
      escapeKey: dismissible,
      outsidePress: dismissible,
      capture: false,
    });

    const role = useRole(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismissal,
      role,
    ]);

    const _onContentClick = (
      e:
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.KeyboardEvent<HTMLDivElement>
    ): void => {
      const clickedElement = e.target;
      if (
        contentCloseElQuery &&
        (clickedElement as HTMLElement).matches(contentCloseElQuery)
      ) {
        e.preventDefault();
        onClose?.(e);
        setOpen(false);
      }
    };

    const renderPopoverContent = (): ReactElement => {
      return (
        <ConditionalWrapper
          condition={!!portalWrapperClassName}
          wrapper={(children): ReactElement => (
            <div className={portalWrapperClassName}>{children}</div>
          )}
        >
          <div
            id={id}
            aria-modal={true}
            role="dialog"
            className={classNames(
              "spark-popover__content",
              { active: open },
              className
            )}
            ref={refs.setFloating}
            {...getFloatingProps()}
            {...rest}
            onClick={_onContentClick}
            onKeyDown={(e): void => {
              if (e.key === "Enter" || e.key === " ") {
                _onContentClick(e);
              }
            }}
            style={{ ...floatingStyles, ...rest.style }}
          >
            {(closeButton || (!dismissible && closeButton !== false)) && (
              <a
                role="button"
                className="spark-popover__close"
                aria-label="Close"
                title="Close"
                tabIndex={0}
                onClick={(e): void => {
                  onClose?.(e);
                  setOpen(false);
                }}
                onKeyDown={(e): void => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClose?.(e);
                    setOpen(false);
                  }
                }}
              />
            )}
            {children}
            <span
              ref={arrowRef}
              id="arrow"
              className="spark-popover__caret"
              data-position={arrowDirection}
              style={getArrowCoords(middlewareData, arrowDirection)}
            ></span>
          </div>
        </ConditionalWrapper>
      );
    };

    const additionalProps = isBadge
      ? { _privateProps: { hasPopover: true } }
      : {};

    const renderPopoverTrigger = (): ReactElement | null => {
      if (React.isValidElement(toggleEl)) {
        return (
          <div
            style={toggleElWrapperStyle}
            className={classNames(
              "spark-popover",
              {
                "popover-active": open,
              },
              toggleElWrapperClassName
            )}
            ref={toggleElWrapperRef}
          >
            {cloneElement(toggleEl as ReactElement, {
              ...getReferenceProps({
                ref: toggleElRef,
                ...(toggleEl as ReactElement).props,
                ...additionalProps,
                className: classNames(
                  get(toggleEl, "props.className"),
                  "spark-popover__toggle",
                  { active: open }
                ),
                onClick(e) {
                  // @TODO: on V2 remove Popover's onClick redirection to toggleEl
                  const toggleOnClick = (toggleEl as ReactElement).props
                    .onClick;
                  const popoverDeprecatedOnClick = onClick;
                  toggleOnClick
                    ? toggleOnClick?.(e)
                    : popoverDeprecatedOnClick?.(e);
                  setOpen(!open);
                },
                "data-state": open ? "open" : "closed",
              }),
            })}
          </div>
        );
      } else return null;
    };

    return (
      <>
        {renderPopoverTrigger()}
        {open && (
          <FloatingPortal>
            <FloatingFocusManager
              context={context}
              modal={true}
              visuallyHiddenDismiss={!closeButton}
            >
              {renderPopoverContent()}
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </>
    );
  }
);

Popover.displayName = "Popover";
